import { SEO } from "../components/SEO";

const Terms = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <SEO
        title="Terms of Service | Compliance Management Systems"
        description="Terms of Service for Compliance Management Systems (Comply-CMS). Review our terms governing the use of our consulting, training, and auditing services."
        url="/terms"
      />

      {/* Header */}
      <section className="bg-charcoal-darker text-white py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-4">Terms of Service</h1>
          <p className="text-slate-300">Effective Date: January 15, 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="prose prose-slate max-w-none">
            <p className="lead text-lg text-slate-600 mb-8">
              These Terms of Service ("<strong>Terms</strong>") govern your access to and use of the Comply-CMS website and Services. By accessing or using the Services, you agree to be bound by these Terms.
            </p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">1. Use of Services</h2>
            <p className="text-slate-600 mb-4">You agree to use our Services:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>For lawful and professional purposes only</li>
              <li>In accordance with all applicable laws and regulations</li>
              <li>Without infringing intellectual property or confidentiality rights</li>
            </ul>
            <p className="text-slate-600">Unauthorized use, misuse, or interference with the Services is strictly prohibited.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">2. Professional Disclaimer</h2>
            <p className="text-slate-600">Our consulting, training, auditing, and advisory services are provided based on recognized standards, best practices, and professional judgment. Implementation outcomes depend on organizational context, commitment, and operational practices. We do not guarantee certification, regulatory approval, or specific business results.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">3. Intellectual Property</h2>
            <p className="text-slate-600 mb-4">All website content, including text, graphics, logos, templates, training materials, and methodologies, is the intellectual property of Comply-CMS or its licensors and is protected by applicable intellectual property laws.</p>
            <p className="text-slate-600">You may not copy, reproduce, distribute, or modify content without prior written permission, except for internal, non-commercial use where expressly permitted.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">4. Client Materials and Confidentiality</h2>
            <p className="text-slate-600">Any materials provided by clients remain the property of the client. We treat all client information as confidential and use it solely for the agreed purpose, subject to legal and professional obligations.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">5. Payments and Fees</h2>
            <p className="text-slate-600 mb-4">Where applicable:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Fees are as agreed in proposals, contracts, or invoices</li>
              <li>Payments must be made within the agreed timeframe</li>
              <li>Late or non-payment may result in service suspension</li>
            </ul>
            <p className="text-slate-600">All fees are exclusive of applicable taxes unless stated otherwise.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">6. Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">To the maximum extent permitted by law, Comply-CMS shall not be liable for:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Indirect, incidental, consequential, or special damages</li>
              <li>Loss of profits, data, or business opportunities</li>
            </ul>
            <p className="text-slate-600">Our total liability shall not exceed the fees paid for the specific service giving rise to the claim.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">7. Indemnification</h2>
            <p className="text-slate-600">You agree to indemnify and hold harmless Comply-CMS from any claims, losses, or damages arising from your misuse of the Services or violation of these Terms.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">8. Service Availability</h2>
            <p className="text-slate-600">We strive to maintain availability of the website and Services but do not guarantee uninterrupted or error-free access. We may suspend or modify Services for maintenance, security, or operational reasons.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">9. Termination</h2>
            <p className="text-slate-600">We reserve the right to suspend or terminate access to the Services for violation of these Terms or applicable laws, without prejudice to other rights or remedies.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">10. Governing Law</h2>
            <p className="text-slate-600">These Terms shall be governed by and construed in accordance with applicable laws of the jurisdiction in which Comply-CMS operates, without regard to conflict of law principles.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">11. Changes to Terms</h2>
            <p className="text-slate-600">We may revise these Terms from time to time. Continued use of the Services after changes become effective constitutes acceptance of the revised Terms.</p>

            <hr className="my-8 border-slate-200" />

            <h2 className="font-heading font-bold text-2xl text-primary mt-10 mb-4">12. Contact Information</h2>
            <p className="text-slate-600 mb-4">For questions regarding these Terms:</p>
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="font-heading font-semibold text-primary mb-2">Compliance Management Systems (Comply-CMS)</p>
              <p className="text-slate-600">Email: <a href="mailto:admin@comply-cms.com" className="text-brand hover:underline">admin@comply-cms.com</a></p>
              <p className="text-slate-600">Website: <a href="https://www.comply-cms.com" className="text-brand hover:underline">https://www.comply-cms.com</a></p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
